/**
 * Swiper Slice
 *
 * 3 slides at a time, centered; left and right slides 75% visible.
 */

import React, { useRef } from "react";
import { graphql } from "gatsby";
import { Autoplay, Navigation } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";
import { PrismicRichText } from "@prismicio/react";
import { isFilled } from "@prismicio/client";
import Icon from "../../components/Icon";
import "swiper/css";
import "swiper/css/effect-coverflow";
import * as styles from "./SwiperSlider.module.scss";

export const SwiperSlider = ({ slice }) => {
  const { color_scheme } = slice?.primary || {};
  const items = slice?.items || [];
  const videoRefs = useRef([]);

  const handleSlideChange = (swiper) => {
    const realIndex =
      typeof swiper.realIndex === "number" ? swiper.realIndex : swiper.activeIndex;

    videoRefs.current.forEach((video, index) => {
      if (!video) return;

      const isActive = index === realIndex;
      video.dataset.playing = isActive ? "true" : "false";

      if (isActive) {
        const playPromise = video.play();
        if (playPromise && typeof playPromise.catch === "function") {
          playPromise.catch(() => {});
        }
      } else {
        video.pause();
        video.currentTime = 0;
      }
    });
  };

  const handleSwiperInit = (swiper) => {
    handleSlideChange(swiper);
  };

  if (!items.length) return null;

  return (
    <section className={styles.slider} data-color-scheme={color_scheme || "white"} id="slider">
      <div className={`page-width py-08 ${styles.container}`}>
        <Swiper
          modules={[Autoplay, Navigation]}
          className={styles.swiper}
          autoplay={items.length > 1 ? { delay: 10000, disableOnInteraction: true } : false}
          slidesPerView="auto"
          spaceBetween={24}
          centeredSlides
          loop={items.length >= 3}
          breakpoints={{
            320: { spaceBetween: 16 },
            768: { spaceBetween: 24 },
          }}
          navigation={items.length > 1 ? {
            prevEl: ".swiper-button-prev",
            nextEl: ".swiper-button-next",
          } : false}
          onSlideChange={handleSlideChange}
          onInit={handleSwiperInit}
        >
          {items.map((item, index) => {
            const mediaType = item.slide_media_type;
            const image = item.slide_image;
            const videoPoster = item.slide_video_poster;
            const videoUrl = item.slide_video_url;
            const heading = item.slide_heading;
            const copy = item.slide_copy;

            return (
              <SwiperSlide key={index} className={styles.slide}>
                <div className={styles.slideInner}>
                  <div className={styles.slideMedia}>
                    {mediaType === "image" && image?.url && (
                      <img src={image.url} alt={heading || ""} />
                    )}
                    {mediaType === "video" && videoUrl && (
                      <>
                        {videoPoster?.url && (
                          <img src={videoPoster.url} alt={heading || ""} />
                        )}
                        <video
                          ref={(el) => {
                            videoRefs.current[index] = el;
                          }}
                          src={videoUrl}
                          poster={videoPoster?.url}
                          data-playing="false"
                          muted
                          loop
                          playsInline
                          preload="metadata"
                        />
                      </>
                    )}
                  </div>
                  {(heading || isFilled.richText(copy)) && (
                    <div className={styles.slideContent}>
                      {heading && (
                        <h3 className={styles.slideHeading}>{heading}</h3>
                      )}
                      {isFilled.richText(copy) && (
                        <div className={styles.slideCopy}>
                          <PrismicRichText field={copy.richText} />
                        </div>
                      )}
                    </div>
                  )}
                </div>
              </SwiperSlide>
            );
          })}
          {items.length > 1 && (<>
            <button aria-label="Previous" className={`${styles.navButton} swiper-button-prev`} data-direction="previous">
              <Icon name="chevronLeft" size="2.8rem" />
            </button>
            <button aria-label="Next" className={`${styles.navButton} swiper-button-next`} data-direction="next">
              <Icon name="chevronRight" size="2.8rem" />
            </button>
          </>)}
        </Swiper>
      </div>
    </section>
  );
};

export const query = graphql`
  fragment AboutPageDataBodySwiperSlider on PrismicAboutPageDataBodySwiperSlider {
    primary {
      color_scheme
    }
    items {
      slide_media_type
      slide_image {
        url
        alt
      }
      slide_video_poster {
        url
        alt
      }
      slide_video_url
      slide_heading
      slide_copy {
        richText
        text
      }
    }
  }
`;
